import React, { useState } from 'react';
import styled from 'styled-components';
import { PageCard } from 'src/common/PageCard';
import { Poppins } from 'src/common/Poppins';
import CheckBox from 'src/components/form/CheckBox/CheckBox';
import { SparkleSvg } from 'src/assets/dynamicSvg';
import { useRiskIntel } from '../useRiskIntel';
import { useLoadingAction } from '../useLoadingAction';
import { PrimaryButton, SecondaryButton } from '../comps/SuggestionButtons';
import { StatusPill } from '../comps/StatusPill';

const Card = styled(PageCard)`
  background: ${({ theme }) => `${theme.colors.aliceBlue}4d`};
  border-radius: 8px;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 6px;
  overflow: hidden;
`;

const Cell = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.stroke};
  border-left: 1px solid ${({ theme }) => theme.colors.stroke};

  &:nth-child(1),
  &:nth-child(2) {
    border-top: none;
  }
  &:nth-child(odd) {
    border-left: none;
  }
`;

const TextCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CommitRow = styled.div`
  display: flex;
  gap: 24px;

  & > * {
    flex: 1 1 0;
  }
`;

export const ImpactSelectStep: React.FC = () => {
  const { session, pending, submitImpacts, changeImpacts } = useRiskIntel();
  const { loadingKey, run } = useLoadingAction();
  const impacts = session?.impacts ?? [];

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(impacts.filter((impact) => impact.selected).map((impact) => impact.id)),
  );

  // Committed once submitted (mining), or formulas mined/accepted.
  const confirmed =
    session?.status === 'mining_impacts' || impacts.some((impact) => impact.formula) || !!session?.impactsAccepted;

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Card>
      <Eyebrow>
        <SparkleSvg size={24} />
        <Poppins px={16} weight={600} color="prussianBlue" css="text-transform: uppercase;">
          Select relevant impacts
        </Poppins>
      </Eyebrow>

      <Poppins px={16} color="cflowerBlue" css="line-height: 1.7;">
        Based on the ransomware scenario you defined, which of these additional impacts are relevant to your
        organisation? Select all that apply.
      </Poppins>

      <Grid>
        {impacts.map((impact) => (
          <Cell key={impact.id}>
            <CheckBox
              isChecked={selected.has(impact.id)}
              disabled={confirmed}
              onClick={() => toggle(impact.id)}
              dataCy={`impact-${impact.id}`}
            />
            <TextCol>
              <Poppins px={14} weight={500} color="prussianBlue">
                {impact.name}
              </Poppins>
              <Poppins px={14} color="cflowerBlue" css="line-height: 1.6;">
                {impact.description}
              </Poppins>
            </TextCol>
          </Cell>
        ))}
      </Grid>

      {confirmed ? (
        <CommitRow>
          <StatusPill label="Confirmed" />
          <SecondaryButton
            label="Change selection"
            fullWidth
            loading={loadingKey === 'change'}
            disabled={pending}
            onClick={() => run('change', () => changeImpacts())}
          />
        </CommitRow>
      ) : (
        <PrimaryButton
          label="Confirm selection"
          fullWidth
          loading={loadingKey === 'confirm'}
          disabled={pending || selected.size === 0}
          onClick={() => run('confirm', () => submitImpacts([...selected]))}
        />
      )}
    </Card>
  );
};
